import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaCalendarAlt,
  FaListAlt,
  FaClipboardList,
  FaPhoneAlt,
  FaEnvelope,
  FaLock,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
} from "react-icons/fa";

export default function PpdbInfo() {
  const [ppdbList, setPpdbList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, "ppdb"), orderBy("judul", "asc"));
      const snap = await getDocs(q);
      setPpdbList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    fetchData();
  }, []);

  /* ================= STATUS PENDAFTARAN ================= */
  const getStatusPendaftaran = (jadwal = []) => {
    if (!Array.isArray(jadwal)) return "TUTUP";

    const pendaftaran = jadwal.find(j =>
      j.tahap?.toLowerCase().includes("pendaftaran")
    );

    if (!pendaftaran?.mulai || !pendaftaran?.selesai) return "TUTUP";

    const today = new Date();
    const mulai = new Date(pendaftaran.mulai);
    const selesai = new Date(pendaftaran.selesai);

    if (today < mulai) return "BELUM";
    if (today > selesai) return "TUTUP";
    return "BUKA";
  };

  /* ================= COUNTDOWN ================= */
  const getCountdown = (jadwal = []) => {
    const pendaftaran = jadwal.find(j =>
      j.tahap?.toLowerCase().includes("pendaftaran")
    );
    if (!pendaftaran?.mulai) return null;

    const today = new Date();
    const mulai = new Date(pendaftaran.mulai);
    const diff = Math.ceil((mulai - today) / (1000 * 60 * 60 * 24));

    if (diff > 0) return `H-${diff}`;
    return null;
  };

  const handleDaftarOnline = (status) => {
    if (status === "BUKA") {
      navigate("/login");
      return;
    }

    if (status === "BELUM") {
      Swal.fire({
        icon: "info",
        title: "Pendaftaran Belum Dibuka",
        text: "Silakan mendaftar sesuai jadwal yang telah ditentukan.",
      });
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Pendaftaran Ditutup",
      text: "Masa pendaftaran PPDB telah berakhir.",
    });
  };

  if (loading) {
    return (
      <p className="text-center mt-5 fw-semibold">
        Memuat informasi PPDB...
      </p>
    );
  }

  return (
    <section className="py-5 bg-light">
      <div className="container">
        <h3 className="fw-bold mb-5 text-center text-primary">
          Informasi PPDB
        </h3>

        {ppdbList.map(item => {
          const status = getStatusPendaftaran(item.jadwal);
          const countdown = getCountdown(item.jadwal);

          return (
            <div
              key={item.id}
              className="card shadow-lg p-4 mb-5 rounded-4 border-0"
            >
              {/* ================= HEADER ================= */}
              <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                <div>
                  <h4 className="fw-bold mb-0">{item.judul}</h4>
                  <small className="text-muted">
                    Penerimaan Peserta Didik Baru
                  </small>
                </div>

                <div className="text-end">
                  <span
                    className={`badge px-3 py-2 rounded-pill fs-6 ${
                      status === "BUKA"
                        ? "bg-success"
                        : status === "BELUM"
                        ? "bg-warning text-dark"
                        : "bg-danger"
                    }`}
                  >
                    {status === "BUKA" && <><FaCheckCircle className="me-1" /> Dibuka</>}
                    {status === "BELUM" && <><FaLock className="me-1" /> Belum Dibuka</>}
                    {status === "TUTUP" && <><FaTimesCircle className="me-1" /> Ditutup</>}
                  </span>

                  {countdown && status === "BELUM" && (
                    <div className="mt-1 text-warning fw-semibold">
                      <FaHourglassHalf className="me-1" />
                      {countdown}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-muted">{item.deskripsi}</p>

              {/* ================= JADWAL ================= */}
              {Array.isArray(item.jadwal) && (
                <div className="mb-4">
                  <h5 className="fw-semibold mb-3">
                    <FaCalendarAlt className="me-2 text-primary" />
                    Jadwal PPDB
                  </h5>

                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead className="table-primary text-center">
                        <tr>
                          <th>Tahap</th>
                          <th>Mulai</th>
                          <th>Selesai</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.jadwal.map((j, i) => (
                          <tr
                            key={i}
                            className={
                              j.tahap?.toLowerCase().includes("pendaftaran")
                                ? "table-success fw-semibold"
                                : ""
                            }
                          >
                            <td>{j.tahap}</td>
                            <td className="text-center">{j.mulai || "-"}</td>
                            <td className="text-center">{j.selesai || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <small className="text-muted">
                    * Tahap <b>Pendaftaran</b> menentukan tombol daftar online
                  </small>
                </div>
              )}

              {/* ================= PERSYARATAN ================= */}
              {Array.isArray(item.persyaratan) && (
                <div className="mb-4 p-3 rounded-3 bg-white shadow-sm">
                  <h5 className="fw-semibold mb-2">
                    <FaListAlt className="me-2 text-primary" />
                    Persyaratan
                  </h5>
                  <ul className="mb-0">
                    {item.persyaratan.map((p, i) => (
                      <li key={i} className="mb-1">{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ================= MEKANISME ================= */}
              {Array.isArray(item.mekanisme) && (
                <div className="mb-4 p-3 rounded-3 bg-light border">
                  <h5 className="fw-semibold mb-3">
                    <FaClipboardList className="me-2 text-primary" />
                    Mekanisme Pendaftaran
                  </h5>
                  <ol className="mb-0">
                    {item.mekanisme.map((m, i) => (
                      <li key={i} className="mb-2">{m}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* ================= KONTAK ================= */}
              {item.kontak && (
                <div className="mb-3">
                  <h6 className="fw-semibold text-secondary">Kontak</h6>
                  <div>
                    {item.kontak.toLowerCase().includes("email")
                      ? <FaEnvelope className="me-2 text-primary" />
                      : <FaPhoneAlt className="me-2 text-primary" />}
                    {item.kontak}
                  </div>
                </div>
              )}

              {/* ================= BUTTON ================= */}
              <button
                className={`btn btn-lg w-100 fw-semibold py-3 ${
                  status === "BUKA"
                    ? "btn-primary"
                    : status === "BELUM"
                    ? "btn-secondary"
                    : "btn-danger"
                }`}
                disabled={status !== "BUKA"}
                onClick={() => handleDaftarOnline(status)}
              >
                {status === "BUKA" && "📝 Daftar Online Sekarang"}
                {status === "BELUM" && <><FaLock className="me-2" /> Pendaftaran Belum Dibuka</>}
                {status === "TUTUP" && "❌ Pendaftaran Ditutup"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
